/**
 * Settings > Claude: "Use DCA in Claude" (2026-10-03).
 *
 * Self-serve MCP connections: a user creates a connector link, pastes it into
 * claude.ai / Claude Desktop (Settings > Connectors > Add custom connector) or
 * Claude Code (`claude mcp add`), and can revoke it here. The raw link is
 * shown ONCE (the server keeps only its hash). Backend: /api/settings/mcp
 * (leads worker routes/settings-mcp.ts).
 *
 * Basic = read-only connections; Pro may also "Let Claude write and send messages".
 * Sending also needs DealCloser's global switch, so a banner explains when
 * it is paused.
 */

import { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
  AlertTriangle,
  Bot,
  Check,
  Copy,
  Loader2,
  Plus,
  ShieldCheck,
  ShieldOff,
  Trash2,
  BookOpen,
  CalendarClock,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { FieldHint } from '@/components/field-hint'
import { fmtDate, fmtDistanceToNow } from '@/lib/dates'
import { AskClaude, StarterKit, StarterKitLimits } from './claude-starter-kit'
import {
  EXPIRY_OPTIONS,
  activeConnections,
  claudeCodeCommand,
  routinePrompt,
  type McpConnection,
  type McpCreateResponse,
} from './mcp-helpers'
import {
  useCreateMcpConnection,
  useMcpConnections,
  useRevokeMcpConnection,
  useUpdateMcpConnection,
} from './use-mcp'

export function CopyButton({ text, label = 'Copy' }: { readonly text: string; readonly label?: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={() => {
        void navigator.clipboard.writeText(text).then(() => {
          setCopied(true)
          setTimeout(() => setCopied(false), 1500)
        })
      }}
    >
      {copied ? <Check className="mr-1 h-3.5 w-3.5" /> : <Copy className="mr-1 h-3.5 w-3.5" />}
      {copied ? 'Copied' : label}
    </Button>
  )
}

// The link carries its own access key, so the connector needs no sign-in. Picking a
// sign-in option instead is the setup that failed in the owner's walkthrough (10-21).
const NO_SIGN_IN = 'Choose No sign in, then click Add. Your link already carries its own access key.'

// Tools default to "Needs approval", which stalls a scheduled run waiting for a click
// that never comes (owner's Loom, 10-24, 05:22).
const ALWAYS_ALLOW = 'Open the DealCloser connector and set its tools to Always allow, so scheduled runs never stop to ask you.'

/** Step-by-step setup for each Claude app. Shown right after a connection is created. */
export function ClaudeSetupSteps({ connectorUrl }: { readonly connectorUrl: string }) {
  const command = claudeCodeCommand(connectorUrl)
  return (
    <Tabs defaultValue="web">
      <TabsList className="flex-wrap">
        <TabsTrigger value="web">claude.ai</TabsTrigger>
        <TabsTrigger value="desktop">Claude Desktop</TabsTrigger>
        <TabsTrigger value="code">Claude Code</TabsTrigger>
      </TabsList>
      <TabsContent value="web">
        <ol className="list-decimal space-y-1 pl-5 text-sm text-muted-foreground">
          <li>Open claude.ai and go to Settings, then Connectors.</li>
          <li>Click Add custom connector.</li>
          <li>Name it DealCloser, paste your connector link as the URL and click Continue.</li>
          <li>{NO_SIGN_IN}</li>
          <li>{ALWAYS_ALLOW}</li>
          <li>Turn it on in a chat from the tools menu.</li>
        </ol>
      </TabsContent>
      <TabsContent value="desktop">
        <ol className="list-decimal space-y-1 pl-5 text-sm text-muted-foreground">
          <li>Open Claude Desktop and go to Settings, then Connectors.</li>
          <li>Click Add custom connector.</li>
          <li>Name it DealCloser, paste your connector link as the URL and click Continue.</li>
          <li>{NO_SIGN_IN} Connectors you add on claude.ai also show up here.</li>
          <li>{ALWAYS_ALLOW}</li>
        </ol>
      </TabsContent>
      <TabsContent value="code">
        <p className="mb-2 text-sm text-muted-foreground">Run this in your terminal:</p>
        <div className="flex items-start gap-2">
          <code className="block flex-1 break-all rounded-md bg-muted px-3 py-2 text-xs">{command}</code>
          <CopyButton text={command} />
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          This adds DealCloser on this computer only. To run your messaging on a schedule in the cloud, also add the
          link on claude.ai (Settings, then Connectors).
        </p>
      </TabsContent>
    </Tabs>
  )
}

function CreateConnectionDialog({
  open,
  onOpenChange,
  canWrite,
}: {
  readonly open: boolean
  readonly onOpenChange: (open: boolean) => void
  readonly canWrite: boolean
}) {
  const create = useCreateMcpConnection()
  const [label, setLabel] = useState('My Claude')
  const [days, setDays] = useState(90)
  const [write, setWrite] = useState(false)
  const [created, setCreated] = useState<McpCreateResponse | null>(null)

  function close(next: boolean) {
    if (!next) {
      // The link is gone for good once the dialog closes.
      setCreated(null)
      setLabel('My Claude')
      setDays(90)
      setWrite(false)
      create.reset()
    }
    onOpenChange(next)
  }

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="flex max-h-[85vh] flex-col overflow-hidden sm:max-w-lg">
        {!created ? (
          <>
            <DialogHeader>
              <DialogTitle>Create a Claude connection</DialogTitle>
              <DialogDescription>
                You get a private link to paste into Claude. Anyone with the link can use your DealCloser
                account through Claude, so treat it like a password.
              </DialogDescription>
            </DialogHeader>
            <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto py-2">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="mcp-label">Name</Label>
                <Input
                  id="mcp-label"
                  value={label}
                  maxLength={60}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="For example: Work laptop"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="mcp-days">Expires after</Label>
                <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
                  <SelectTrigger id="mcp-days">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {EXPIRY_OPTIONS.map((o) => (
                      <SelectItem key={o.days} value={String(o.days)}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-start gap-2 rounded-lg border p-3">
                <Checkbox
                  id="mcp-write"
                  checked={write}
                  disabled={!canWrite}
                  onCheckedChange={(v) => setWrite(v === true)}
                />
                <div className="space-y-1">
                  <Label htmlFor="mcp-write" className="flex items-center gap-1.5">
                    Let Claude write and send messages
                    <FieldHint label="What does this allow?">
                      Claude can write LinkedIn messages (DealCloser quality checks each one and saves it to
                      your review queue), reject drafts, hand campaigns to Claude, record how a conversation
                      ended and pause campaigns. Sending, approving drafts, booking meetings and resuming
                      campaigns also need DealCloser to have sending switched on. Your daily limits and
                      sending hours always apply.
                    </FieldHint>
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    {canWrite
                      ? 'Leave this off if you only want Claude to read your data.'
                      : 'On Basic, Claude can read your data. Letting Claude send messages needs the Pro plan.'}
                  </p>
                </div>
              </div>
              {create.error instanceof Error && <p className="text-sm text-destructive">{create.error.message}</p>}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => close(false)} disabled={create.isPending}>
                Cancel
              </Button>
              <Button
                onClick={() =>
                  create.mutate({ label: label.trim(), write: canWrite && write, days }, { onSuccess: setCreated })
                }
                disabled={create.isPending || !label.trim()}
              >
                {create.isPending && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
                Create connection
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Your connector link</DialogTitle>
              <DialogDescription>
                Copy it now. For your security it is shown only once. If you lose it, revoke this connection
                and create a new one.
              </DialogDescription>
            </DialogHeader>
            <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto py-2">
              <div className="flex items-start gap-2">
                <code className="block flex-1 break-all rounded-md bg-muted px-3 py-2 text-xs" data-testid="mcp-connector-url">
                  {created.connector_url}
                </code>
                <CopyButton text={created.connector_url} />
              </div>
              {created.note && (
                <p className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-300">
                  {created.note}
                </p>
              )}
              <ClaudeSetupSteps connectorUrl={created.connector_url} />
              <p className="text-xs text-muted-foreground">
                Then ask Claude something like &quot;How are my DealCloser campaigns doing?&quot; to check it works.
              </p>
            </div>
            <DialogFooter>
              <Button onClick={() => close(false)}>Done</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

/**
 * Turning on "Claude checks its own messages" (skip_qg): the user must tick
 * that they are responsible before the request is even possible. Turning it
 * off needs no confirmation.
 */
function SkipQgDialog({
  conn,
  open,
  onOpenChange,
}: {
  readonly conn: McpConnection
  readonly open: boolean
  readonly onOpenChange: (open: boolean) => void
}) {
  const update = useUpdateMcpConnection()
  const [accepted, setAccepted] = useState(false)
  function close(next: boolean) {
    if (!next) {
      setAccepted(false)
      update.reset()
    }
    onOpenChange(next)
  }
  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-400" />
            Let Claude skip the quality check?
          </DialogTitle>
          <DialogDescription>For the connection &ldquo;{conn.label || 'Unnamed connection'}&rdquo;.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <p>
            Normally DealCloser scores every message your Claude writes and stops anything with invented claims, a
            made-up offer, a stock opener, a wrong link or a pushy tone. With this on, your Claude can send messages{' '}
            <span className="font-semibold">without that check</span> when you ask it to (for example with the{' '}
            <span className="font-mono text-xs">skip-qg</span> prompt).
          </p>
          <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-200">
            You are responsible for every message sent this way. DealCloser will not review them, and they go out from
            your LinkedIn account under your name. Daily limits, sending hours, opt-outs, blocked leads and the
            DealCloser sending switch still apply.
          </div>
          <div className="flex items-start gap-2">
            <Checkbox id={`skipqg-${conn.id}`} checked={accepted} onCheckedChange={(v) => setAccepted(v === true)} />
            <Label htmlFor={`skipqg-${conn.id}`} className="text-xs leading-snug">
              I understand these messages skip DealCloser&rsquo;s quality check and I am responsible for what is sent.
            </Label>
          </div>
          {update.error instanceof Error && <p className="text-xs text-destructive">{update.error.message}</p>}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => close(false)}>Cancel</Button>
          <Button
            variant="destructive"
            disabled={!accepted || update.isPending}
            onClick={() => update.mutate({ id: conn.id, skip_qg: true, acknowledge: true }, { onSuccess: () => close(false) })}
          >
            {update.isPending && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
            Turn on
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function ConnectionRow({ conn, canWrite }: { readonly conn: McpConnection; readonly canWrite: boolean }) {
  const update = useUpdateMcpConnection()
  const revoke = useRevokeMcpConnection()
  const [confirming, setConfirming] = useState(false)
  const [skipDialog, setSkipDialog] = useState(false)
  const error =
    (update.error instanceof Error ? update.error.message : null) ??
    (revoke.error instanceof Error ? revoke.error.message : null)

  return (
    <div className="rounded-lg border p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <span className="truncate text-sm font-semibold">{conn.label || 'Unnamed connection'}</span>
          {conn.expired ? (
            <Badge variant="destructive" className="text-[10px]">Expired</Badge>
          ) : conn.write ? (
            <Badge variant="secondary" className="text-[10px]">Can send</Badge>
          ) : (
            <Badge variant="outline" className="text-[10px]">Read only</Badge>
          )}
          {!conn.expired && conn.write && conn.skip_qg && (
            <Badge variant="outline" className="border-amber-500/50 text-[10px] text-amber-300">
              <ShieldOff className="mr-1 h-3 w-3" />
              Skips quality check
            </Badge>
          )}
          <span className="font-mono text-[10px] text-muted-foreground">{conn.prefix}</span>
        </div>
        <div className="flex items-center gap-2">
          {!conn.expired && canWrite && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              disabled={update.isPending}
              onClick={() => update.mutate({ id: conn.id, write: !conn.write })}
            >
              {update.isPending && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
              {conn.write ? 'Make read only' : 'Let Claude send'}
            </Button>
          )}
          {!conn.expired && canWrite && conn.write && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              disabled={update.isPending}
              onClick={() => (conn.skip_qg ? update.mutate({ id: conn.id, skip_qg: false }) : setSkipDialog(true))}
            >
              {conn.skip_qg ? 'Use quality check' : 'Let Claude check itself'}
            </Button>
          )}
          {confirming ? (
            <>
              <Button
                variant="destructive"
                size="sm"
                className="h-7 text-xs"
                disabled={revoke.isPending}
                onClick={() => revoke.mutate(conn.id)}
              >
                {revoke.isPending && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
                Confirm revoke
              </Button>
              <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setConfirming(false)}>
                Cancel
              </Button>
            </>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs text-destructive hover:text-destructive"
              onClick={() => setConfirming(true)}
            >
              <Trash2 className="mr-1 h-3 w-3" />
              Revoke
            </Button>
          )}
        </div>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        Created {fmtDate(conn.created_at, 'MMM d, yyyy')} ·{' '}
        {conn.expires_at ? `${conn.expired ? 'Expired' : 'Expires'} ${fmtDate(conn.expires_at, 'MMM d, yyyy')}` : 'No expiry'} ·{' '}
        {conn.last_used_at ? `Last used ${fmtDistanceToNow(conn.last_used_at, { addSuffix: true })}` : 'Not used yet'}
      </p>
      {error && <p className="mt-1 text-xs text-destructive">{error}</p>}
      <SkipQgDialog conn={conn} open={skipDialog} onOpenChange={setSkipDialog} />
    </div>
  )
}

/** The ready-made routine prompt with copy buttons, so users do not write their own. */
function RoutinePrompt() {
  const prompt = routinePrompt('send')
  return (
    <div className="mt-4 space-y-2 rounded-lg border p-3">
      <p className="text-sm font-medium">Ready-made prompt</p>
      <p className="text-xs text-muted-foreground">
        Paste this into Claude to run your messaging, or use it as the instructions of a scheduled task.
      </p>
      <pre className="whitespace-pre-wrap break-words rounded-md bg-muted px-3 py-2 text-xs" data-testid="routine-prompt">
        {prompt}
      </pre>
      <div className="flex flex-wrap gap-2">
        <CopyButton text={prompt} label="Copy prompt" />
        <CopyButton text={routinePrompt('draft')} label="Copy review-first version" />
      </div>
      <p className="text-xs text-muted-foreground">
        The review-first version tells Claude to send nothing: every message it writes waits in the review queue
        at the top of Inbox for you to approve.
      </p>
    </div>
  )
}

/** The "Let Claude run your messaging" guide. Linked from campaigns as #claude-guide. */
function MessagingGuide() {
  return (
    <Card id="claude-guide" className="scroll-mt-4">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <BookOpen className="h-4 w-4" />
          Let Claude run your messaging
        </CardTitle>
        <CardDescription>
          DealCloser still decides who is due and when. Your Claude decides what to say. Every message goes
          through the same quality check, daily limits and review queue as before, and outreach stays inside your
          sending hours.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ol className="list-decimal space-y-2 pl-5 text-sm">
          <li>Create a connection above with &quot;Let Claude write and send messages&quot; turned on (Pro plan).</li>
          <li>Add the link to Claude using the steps shown when you create it.</li>
          <li>
            Open a campaign and set <span className="font-medium">Who writes the messages</span> to{' '}
            <span className="font-medium">Your Claude</span>. DealCloser keeps sending the connection requests
            and stops writing the messages for that campaign.
          </li>
          <li>
            In Claude, say <span className="font-medium">&quot;Do my DealCloser messaging&quot;</span>. Claude
            lists who is due, reads each lead, writes the message, checks it and submits it.
          </li>
          <li>
            Optional: add your skill from the <span className="font-medium">Starter kit</span> below so Claude
            writes in your words, then put it on a schedule (see{' '}
            <span className="font-medium">Run your messaging on a schedule</span>).
          </li>
        </ol>
        <RoutinePrompt />
        <p className="mt-3 text-xs text-muted-foreground">
          If Claude has not written a due message after the hours you choose on the campaign, DealCloser&apos;s
          AI takes it back so leads are not left waiting. You can switch a campaign back to DealCloser AI at
          any time.
        </p>
      </CardContent>
    </Card>
  )
}

/**
 * Where a scheduled "Do my DealCloser messaging" run happens (Eli 2026-10-21,
 * "DCA MCP Guidelines"). Checked against Anthropic's docs 2026-10-22: cloud
 * scheduled tasks use only connectors on the claude.ai account, so a link
 * added with `claude mcp add` alone is invisible to them.
 */
function ScheduleGuide() {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <CalendarClock className="h-4 w-4" />
          Run your messaging on a schedule
        </CardTitle>
        <CardDescription>
          Claude only messages your leads when something asks it to. Pick where that happens.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ul className="list-disc space-y-2 pl-5 text-sm">
          <li>
            <span className="font-medium">In the cloud (recommended).</span> Create a scheduled task on claude.ai,
            or with <code className="rounded bg-muted px-1 text-xs">/schedule</code> in Claude Code. It runs even
            when your computer is off. Add DealCloser on claude.ai (Settings, then Connectors): a link added only
            with <code className="rounded bg-muted px-1 text-xs">claude mcp add</code> stays on your computer, and
            a cloud task can&apos;t use it.
          </li>
          <li>
            <span className="font-medium">On your computer.</span> A scheduled task in Claude Desktop runs only while
            the app is open and your computer is awake.
          </li>
          <li>
            <span className="font-medium">By hand.</span> In any chat, say &quot;Do my DealCloser messaging&quot;
            whenever you like.
          </li>
        </ul>
        <p className="mt-3 text-xs text-muted-foreground">
          A scheduled task can use DealCloser without asking you first. DealCloser still applies your daily limits
          and keeps outreach inside your sending hours.{' '}
          <a
            href="https://code.claude.com/docs/en/routines"
            target="_blank"
            rel="noopener noreferrer"
            className="underline"
          >
            How Claude&apos;s scheduled tasks work
          </a>
        </p>
      </CardContent>
    </Card>
  )
}

export default function ClaudeSettings() {
  const { data, isLoading, error } = useMcpConnections()
  const [dialogOpen, setDialogOpen] = useState(false)
  const { hash } = useLocation()

  // Campaign screens link to /settings?tab=claude#claude-guide; the SPA does
  // not scroll to the anchor on its own once the data has rendered.
  useEffect(() => {
    if (data && hash === '#claude-guide') document.getElementById('claude-guide')?.scrollIntoView({ behavior: 'smooth' })
  }, [data, hash])

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading Claude connections...
      </div>
    )
  }
  if (error || !data) {
    return <p className="text-sm text-destructive">{error instanceof Error ? error.message : 'Could not load Claude connections.'}</p>
  }

  const canWrite = data.plan_access === 'read_write'
  const active = activeConnections(data.connections)
  const atLimit = active.length >= data.max_active

  return (
    <div className="space-y-6" data-tour="settings-claude">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bot className="h-5 w-5" />
            Use DCA in Claude
            <FieldHint label="What is a Claude connection?">
              A private link that lets your own Claude app talk to your DealCloser account. It only ever sees
              your account, and you can revoke it here at any time.
            </FieldHint>
          </CardTitle>
          <CardDescription>
            Connect your own Claude (claude.ai, Claude Desktop or Claude Code) to DealCloser. Ask it how your
            campaigns are doing, what leads replied, or what is waiting in your review queue. On Pro, you can also
            let Claude write your LinkedIn messages.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!data.writes_enabled && (
            <div className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-300">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>Sending through Claude is paused by DealCloser for now. Claude can still write your messages: they wait in your review queue (Inbox) for you to send.</span>
            </div>
          )}
          {!data.eligible && (
            <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                Connecting Claude needs an active subscription. <Link to="/billing" className="underline">Go to billing</Link>
              </span>
            </div>
          )}
          {!canWrite && data.eligible && (
            <p className="text-xs text-muted-foreground">
              On Basic, Claude can read your data. <Link to="/billing" className="underline">Upgrade to Pro</Link> to let
              Claude write and send messages.
            </p>
          )}

          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
              <ShieldCheck className="h-4 w-4" />
              {active.length} of {data.max_active} connections in use
            </p>
            <Button onClick={() => setDialogOpen(true)} disabled={!data.eligible || atLimit}>
              <Plus className="mr-1.5 h-4 w-4" />
              Create connection
            </Button>
          </div>
          {atLimit && (
            <p className="text-xs text-muted-foreground">You have reached the limit. Revoke a connection to create a new one.</p>
          )}

          {data.connections.length === 0 ? (
            <p className="rounded-lg border border-dashed p-4 text-center text-sm text-muted-foreground">
              No connections yet. Create one to use DealCloser from Claude.
            </p>
          ) : (
            <div className="space-y-2">
              {data.connections.map((c) => (
                <ConnectionRow key={c.id} conn={c} canWrite={canWrite} />
              ))}
            </div>
          )}
          <p className="text-xs text-muted-foreground">
            Revoking takes effect within about a minute. Lost a link? Revoke it and create a new one.
          </p>
        </CardContent>
      </Card>

      <AskClaude />

      <MessagingGuide />

      <StarterKit />

      <ScheduleGuide />

      <StarterKitLimits />

      <CreateConnectionDialog open={dialogOpen} onOpenChange={setDialogOpen} canWrite={canWrite} />
    </div>
  )
}
